<template>
    <div>
        <BackButton routeName="appointment-list" />

        <div class="inner-content full">
            <div v-if="!appointmentError && appointment != null">
                <h1>{{$t('appointment_schedule.title')}} {{clientNames}}</h1>
                <div class="info">
                    <div>
                        <DatePicker v-model:date="date" :lockPast="true" color="blue" :disabledDays="closedDays" />
                    </div>
                    <div>
                        <span v-if="appointment.date != null">
                            {{$t('appointment_schedule.planned')}} {{formatAppointmentDate}}
                        </span>
                        <span v-else>{{$t('appointment_schedule.not_planned')}}</span>
                    </div>
                </div>

                <div class="list">
                    <div v-for="slot in slots" :key="slot.time" class="list--item" :class="{selected : isSelectedSlot(slot) }">
                        <button :disabled="slot.free_slots == 0" @click.prevent="scheduleAppointment(slot)">
                            {{slot.time}}
                        </button>
                    </div>

                    <div v-if="slots.length == 0">
                        {{$t('appointment_schedule.noslots')}}
                    </div>
                </div>

                <div class="actions">
                    <button class="btn-basic blue" @click.prevent="showAppointmentList()" :disabled="savingAppointment" >
                        {{$t('appointment_schedule.schedule')}}
                    </button>

                    <div v-if="savingAppointment">
                        {{$t('generic.waiting')}}
                    </div>

                    <span class="date-warning" v-if="datePassed">
                        {{$t('appointment_schedule.date_passed')}}
                    </span>
                </div>
            </div>

            <div v-else>
                <h3 v-if="!loading">{{$t('appointment_schedule.error')}}</h3>
            </div>
        </div>

        <ConfirmPopup v-if="missingDatePopup"
            :title="$t('appointment_schedule.no_date')"
            color="blue"
            :showCancel="false"
            @confirm="missingDatePopup = false"
            yesLabel="ok"
         />

        <Loading v-if="loading" />
    </div>
</template>

<script>
import BackButton from "@/components/app/BackButton.vue";
import DatePicker from "@/components/DatePicker.vue";
import Loading from "@/components/app/Loading.vue";
import ConfirmPopup from "@/components/app/ConfirmPopup.vue";
import FormatDateMixin from "@/mixins/FormatDateMixin";
import CoconMixin from "@/mixins/CoconMixin";
import {
    getDateForPicker,
    isLaterThan,
    areDatesEqual,
    getSlotFromDate,
    newDateFromString,
    isValidDate,
    formatAppointmentNames,
    getWeekDay
} from "@/utils/utils";
import LoginMixin from "@/mixins/LoginMixin";
import {apiStore} from "@/stores/api";
import {measurementStore} from "@/stores/measurement";

export default {
    name : "AppointmentSchedule",

    mixins : [
        CoconMixin,
        FormatDateMixin,
        LoginMixin
    ],

    components: {
        BackButton,
        DatePicker,
        Loading,
        ConfirmPopup
    },

    data() {
        return {
            loading : true,
            appointment: null,
            appointmentError : false,
            date : null,
            slots : [],
            shopHours : [],
            missingDatePopup: false,
            closedDays : [],
            savingAppointment: false
        };
    },

    watch: {
        date() {
            this.getTimeslots();
        },

        cocons() {
            this.getTimeslots();
        }
    },

    computed: {
        clientNames() {
            return this.appointment !== null ? formatAppointmentNames(this.appointment) : "";
        },

        formatAppointmentDate() {
            return this.fullFormatDate(this.appointment.date);
        },

        datePassed() {
            return this.appointment.date !== null && newDateFromString(this.appointment.date) < Date.now();
        }
    },

    async created() {
        try {
            let shop = apiStore().shop;
            if (!shop) {
                shop = await apiStore().getShop(this.loggedInUser.shop.id);
            }
            this.shopHours = JSON.parse(shop.hours);
            for (let i = 0; i < this.shopHours.slots.length; i++) {
                if (this.shopHours.slots[i].length === 0) {
                    this.closedDays.push(i);
                }
            }
            this.date = getDateForPicker(new Date());

            if (this.$route.params.id !== undefined) {
                this.appointment = await apiStore().getAppointment(this.$route.params.id);
            } else {
                this.appointmentError = true;
            }
        } catch (error) {
            console.log("shop error", error);
        }
        finally {
            this.loading = false;
        }

        //update every minute the slots
        const seconds = (new Date()).getSeconds();
        setTimeout(() => {
            setInterval(() => this.checkSlotsWithtime(), 60 * 1000);
            this.$forceUpdate(); //force update for datePassed
        }, (60 - seconds) * 1000);
    },

    methods: {
        async getTimeslots() {
            if (this.cocons > 0 && this.date !== null) {
                const weekDay = getWeekDay(newDateFromString(this.date));
                const dayShopHours = this.shopHours.slots[weekDay];
                const duration = this.shopHours.duration;

                if (dayShopHours.length > 0) {
                    this.slots = await apiStore().getTimeslots(this.date, dayShopHours, duration, this.cocons);
                    this.checkSlotsWithtime();
                } else {
                    this.slots = [];
                }
            }
        },

        async scheduleAppointment(slot) {
            this.savingAppointment = true;
            this.appointment.date = slot !== null ? this.date + " " + slot.time + ":00" : null;
            this.appointment.measurement_done = false;
            this.appointment.ready_for_measurement = true;
            this.appointment = await apiStore().updateAppointment(this.appointment);
            this.savingAppointment = false;

            this.getTimeslots();

            measurementStore().emitAppointmentsSync();
        },

        checkSlotsWithtime() {
            if (areDatesEqual(this.date, new Date())) {
                this.slots = this.slots.filter(x => isLaterThan(x.time, 5));
            }
        },

        isSelectedSlot(slot) {
            if (this.date === null || this.appointment.date === null) return false;

            return this.date !== null
                    && areDatesEqual(this.appointment.date, this.date)
                    && getSlotFromDate(this.appointment.date) === slot.time
                    && !this.appointment.measurement_done;
        },

        showAppointmentList() {
            const date = newDateFromString(this.appointment.date);
            if (isValidDate(date)) {
                this.$router.push({name : "appointment-list"});
            } else {
                this.missingDatePopup = true;
            }
        }

    }
};
</script>

<style lang="scss" scoped>
    .inner-content {
        .info {
            display: flex;
            align-items: center;
            justify-content: space-between;

            @include slideIntroAnimation(basicSlideIntro, 450ms, 450ms);
        }

        .list {
            display: flex;
            flex-direction: row;
            height: 100%;
            flex-wrap: wrap;
            align-content: flex-start;
            @include slideIntroAnimation(basicSlideIntro, 450ms, 650ms);

            &--item {
                margin-right : 1rem;
                width: 6rem;
                transition: background-color 250ms;
                text-align: center;
                padding : 0 ;

                @for $i from 1 through 50 {
                    &:nth-child(#{$i}) {
                        animation : none;

                    }
                }

                button {
                    @extend .reset-btn;
                    font-size: 1.25em;
                    color: $color-blue;
                    text-align: center;
                    width : 100%;

                    &:disabled {
                        opacity: .2;
                    }
                }

                &.selected {
                    background : $color-blue;

                    button {
                        color : white;

                        &:disabled {
                            opacity: 1;
                        }
                    }
                }
            }
        }

        .actions {
            display: flex;
            align-items: center;
            margin-top: 1rem;

            .btn-basic {
                margin-right : 1rem;
            }
        }

        .date-warning {
            margin-left : 1rem;
            color : $color-red;
            font-size : 1.2em;
        }

    }
</style>